import { FC, memo, ReactNode, useCallback, useMemo, useState } from "react";
import PopupMenu from "../../modals/PopupMenu";
import { useTranslation } from "react-i18next";
import DeleteTrashIconSVG from "../../../custom-components/svg/functional-icons/DeleteTrashIconSVG";
import EyeIconSVG from "../../shared/svg/console/EyeIconSVG";
import BaseModal from "../../modals/BaseModal";
import EditPencilIconSVG from "../../../custom-components/svg/functional-icons/EditPencilIconSVG";
import ImagePlaceholderSVG from "../../shared/svg/ImagePlaceholder";
import { IManagement } from "../../../domain/models/Management";
import { Purpose } from "../../../domain/enums/purpose-enum";
import MenuItem from "../../modals/MenuItem";

interface ManagementCardProps {
    item: IManagement;
    onClick?: () => void;
    onDelete?: (id: string) => void;
    onEdit?: (id: string) => void;
}

const ManagementCard: FC<ManagementCardProps> = ({ item, onClick, onDelete, onEdit }) => {
    const { t } = useTranslation();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

    const createdDate = useMemo(() => new Date(item.createdAt).toLocaleDateString(), [item.createdAt]);

    const handleCopyId = useCallback(() => {
        navigator.clipboard.writeText(item.id).catch(() => {
            console.error("Failed to copy ID to clipboard");
        });
    }, [item.id]);

    const handleDelete = useCallback(() => {
        onDelete?.(item.id);
        setDeleteModalVisible(false);
    }, [onDelete, item.id]);

    const openDeleteModal = useCallback(() => setDeleteModalVisible(true), []);
    return (
        <div
            className="bg-white dark:bg-dark-bg-100 rounded-xl shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition relative"
            onClick={onClick}
        >
            <div className="flex justify-center">
                {item.pictureExists(Purpose.AVATAR) ? (
                    <img
                        src={item.getPictureUrl()}
                        alt={item.translations[0].fullName}
                        className="w-full h-40 object-cover"
                    />
                ) : (
                    <ImagePlaceholderSVG fill="#bec6d5" classname="h-40 justify-self-center" />
                )}
            </div>

            <div className="p-4">
                <h2 className="text-lg font-semibold text-light-text-100 dark:text-dark-text-100 mb-2 truncate">
                    {item.translations[0].fullName}
                </h2>
                <p className="text-light-text-100 dark:text-dark-text-300 text-sm mb-2 line-clamp-3">
                    {item.translations[0].position}
                </p>
                <span className="text-xs text-gray-400">{createdDate}</span>
            </div>
            <div className="absolute top-2 right-2 z-10">
                <PopupMenu
                    trigger={
                        <button className="px-3 py-1 text-light-text-100 dark:text-dark-text-100 bg-light-bg-200 dark:bg-dark-bg-700 rounded-lg hover:bg-gray-200 hover:dark:bg-dark-bg-900 shadow">
                            ⋮
                        </button>
                    }
                >
                    <MenuItem
                        onClick={onClick}
                        className="hover:bg-gray-200 hover:dark:bg-dark-bg-700 hover:dark:text-dark-text-100"
                    >
                        <div className="flex items-center gap-1">
                            <EyeIconSVG className="size-5" /> {t("console.management.view")}
                        </div>
                    </MenuItem>

                    <MenuItem
                        onClick={() => onEdit(item.id)}
                        className="hover:bg-gray-200 hover:dark:bg-dark-bg-700 hover:dark:text-dark-text-100"
                    >
                        <div className="flex items-center gap-2">
                            <EditPencilIconSVG iconColor="black" /> {t("console.management.edit")}
                        </div>
                    </MenuItem>

                    <MenuItem
                        onClick={handleCopyId}
                        className="hover:bg-gray-200 hover:dark:bg-dark-bg-700 hover:dark:text-dark-text-100"
                    >
                        <div className="flex items-center gap-2">
                            <EditPencilIconSVG iconColor="black" /> {t("console.management.copy")}
                        </div>
                    </MenuItem>

                    <MenuItem
                        onClick={openDeleteModal}
                        className="text-red-500 hover:bg-red-100 hover:dark:bg-dark-bg-600"
                    >
                        <div className="flex items-center gap-2">
                            <DeleteTrashIconSVG iconColor="red" /> {t("console.management.delete")}
                        </div>
                    </MenuItem>
                </PopupMenu>
            </div>

            <BaseModal
                isVisible={isDeleteModalVisible}
                setIsVisible={setDeleteModalVisible}
                title={t("console.management.deleteConfirmTitle")}
                confirmTitle={t("console.management.delete")}
                cancelTitle={t("console.common.cancel")}
                confirmHandler={handleDelete}
                additionalWrapClassName="w-[400px]"
                additionalConfirmButtonClassName="bg-red-600 hover:bg-red-700"
            >
                <div className="text-center py-4">
                    <p className="text-lg text-light-text-100 dark:text-dark-text-100 mb-2">
                        {t("console.management.deleteConfirmText")}
                    </p>
                    <p className="text-sm text-gray-500">{item.translations[0].fullName}</p>
                </div>
            </BaseModal>
        </div>
    );
};

export default memo(ManagementCard);
